import { Sparkline } from '../../components/Sparkline'
import type { GsrLive, PolarLive } from './sensorLive'

/**
 * Bloque "En vivo" de un sensor: el mismo para la conexión directa por Web
 * Bluetooth (este equipo) y para los sensores del kit que reporta la
 * Raspberry por el relay. Hasta el 2026-09-17 este JSX vivía inline en
 * `DevicePanelContainer` sólo para el directo, y el sensor del kit se veía
 * como JSON crudo (mg: *"unificá esta interfaz para que use lo mismo que se
 * usa abajo en conexión directa por BLE"*). Los números salen de
 * `sensorLive.ts`; acá sólo se dibujan.
 */

export function PolarLivePanel({
  live,
  connected,
  error,
  onReset,
}: {
  live: PolarLive
  connected: boolean
  error: string | null
  onReset?: () => void
}) {
  return (
    <div className="card flex flex-col gap-xs text-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-text-muted">En vivo</div>
        {connected && (
          <button
            type="button"
            className="btn-ghost text-xs"
            title="Vacía trazas, ventana de RR y contadores sin desconectar: para descartar el ruido de los primeros segundos"
            onClick={() => onReset?.()}
          >
            Reiniciar
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-md gap-y-xs">
        <span className="text-text-muted">Frecuencia cardíaca</span>
        <span className="text-right font-mono">
          {live.hr !== null ? `${live.hr} bpm` : '—'}
        </span>
        <span className="text-text-muted">RMSSD (últimos 60 RR)</span>
        <span className="text-right font-mono">
          {live.rmssdMs !== null ? `${live.rmssdMs.toFixed(1)} ms` : '—'}
        </span>
        <span className="text-text-muted">Último RR</span>
        <span className="text-right font-mono">
          {live.lastRrMs.length
            ? `${live.lastRrMs.map((v) => v.toFixed(1)).join(', ')} ms`
            : '—'}
        </span>
        <span className="text-text-muted">Notificaciones / RR</span>
        <span className="text-right font-mono">
          {live.notifies} / {live.rrCount}
        </span>
        <span className="text-text-muted">Cadencia</span>
        <span className="text-right font-mono">
          {live.notifyIntervalMs !== null
            ? `${(live.notifyIntervalMs / 1000).toFixed(2)} s`
            : '—'}
        </span>
        <span className="text-text-muted">Última notificación</span>
        <span className="text-right font-mono">
          {live.lastNotifyAt
            ? new Date(live.lastNotifyAt).toLocaleTimeString('es-AR')
            : '—'}
        </span>
      </div>
      {error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-sm py-xs text-xs text-rose-400">
          {error}
        </div>
      )}
      {connected && live.rmssdMs !== null && live.rmssdMs > 200 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-sm py-xs text-xs text-amber-400">
          RMSSD mayor a 200 ms no es fisiológico: electrodos secos o latidos
          perdidos. Mojá la banda y esperá un minuto.
        </div>
      )}
      {live.hrTrace.length >= 2 && (
        <div>
          <div className="text-xs text-text-muted">HR (últimos {live.hrTrace.length} notifies)</div>
          <Sparkline values={live.hrTrace} />
        </div>
      )}
      {live.rrTrace.length >= 2 && (
        <div>
          <div className="text-xs text-text-muted">RR (ms)</div>
          <Sparkline values={live.rrTrace} />
        </div>
      )}
    </div>
  )
}

export function GsrLivePanel({
  live,
  connected,
  error,
  batteryLevel,
  onReset,
  onDownloadCsv,
}: {
  live: GsrLive
  connected: boolean
  error: string | null
  /** Batería en % cuando la reporta la Pi (el directo trae `live.battery`). */
  batteryLevel?: number | null
  onReset?: () => void
  onDownloadCsv?: () => void
}) {
  return (
    <div className="card flex flex-col gap-xs text-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-text-muted">En vivo</div>
        {connected && (
          <div className="flex items-center gap-xs">
            <button
              type="button"
              className="btn-ghost text-xs"
              title="Descarga una fila por notificación del counter (ADC, tensión, resistencia, conductancia, calidad, SCL) para analizar la señal afuera"
              disabled={(live.quality?.samples ?? 0) === 0}
              onClick={() => onDownloadCsv?.()}
            >
              CSV
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              title="Vacía trazas, ventana del tónico y contadores sin desconectar: para descartar el ruido de los primeros segundos de contacto"
              onClick={() => onReset?.()}
            >
              Reiniciar
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-md gap-y-xs">
        <span className="text-text-muted">Tónico (ADC, mediana 30 s)</span>
        <span className="text-right font-mono">
          {live.tonicUS !== null
            ? `${live.tonicUS.toFixed(3)} µS`
            : live.tonicWindowSamples
              ? `juntando ${live.tonicWindowSamples} válidas…`
              : '—'}
        </span>
        <span className="text-text-muted">Fásico (ADC, última − tónico)</span>
        <span className="text-right font-mono">
          {live.phasicUS !== null
            ? `${live.phasicUS >= 0 ? '+' : ''}${live.phasicUS.toFixed(3)} µS`
            : '—'}
        </span>
        <span className="text-text-muted" title="KPI del firmware: conversión del hardware anterior, media móvil de 30 s. Sólo para comparar; no es medición.">
          SCL (Pico, legacy)
        </span>
        <span className="text-right font-mono text-text-muted">
          {live.sclUS !== null ? `${live.sclUS.toFixed(3)} µS` : '—'}
        </span>
        <span className="text-text-muted" title="KPI del firmware: (uint16)(última − media 30 s); toda desviación negativa envuelve o se lee 0. No es medición.">
          SCR (Pico, legacy)
        </span>
        <span className="text-right font-mono text-text-muted">
          {live.scrUS !== null ? `${live.scrUS.toFixed(3)} µS` : '—'}
        </span>
        <span className="text-text-muted">Rango activo</span>
        <span className="text-right font-mono">
          {live.measurement
            ? `${live.measurement.rangeId} (Rf ${live.measurement.rangeLabel ?? '?'})`
            : '—'}
        </span>
        <span className="text-text-muted">ADC / tensión</span>
        <span className="text-right font-mono">
          {live.measurement
            ? `${live.measurement.adcCode} · ${live.measurement.voltageV.toFixed(3)} V`
            : '—'}
        </span>
        <span className="text-text-muted">Resistencia de piel</span>
        <span className="text-right font-mono">
          {live.measurement?.resistanceOhm != null
            ? `${(live.measurement.resistanceOhm / 1000).toFixed(1)} kΩ ±${((live.measurement.relativeUncertainty ?? 0) * 100).toFixed(1)} %`
            : live.measurement
              ? live.measurement.quality
              : '—'}
        </span>
        <span className="text-text-muted">Conductancia (ADC)</span>
        <span className="text-right font-mono">
          {live.measurement?.conductanceUS != null
            ? `${live.measurement.conductanceUS.toFixed(3)} µS`
            : '—'}
        </span>
        <span className="text-text-muted">Desde el cambio de rango</span>
        <span className="text-right font-mono">
          {live.elapsedSinceRangeChangeMs !== null
            ? `${live.elapsedSinceRangeChangeMs} ms`
            : '—'}
        </span>
        <span className="text-text-muted">Hora en origen (Pico)</span>
        <span className="text-right font-mono">
          {live.measurement
            ? live.sampleTimeMs !== null
              ? `${(live.sampleTimeMs / 1000).toFixed(1)} s`
              : 'no (firmware de 9 bytes)'
            : '—'}
        </span>
        <span className="text-text-muted">Muestras inválidas</span>
        <span className="text-right font-mono">
          {live.totalSamples
            ? `${live.invalidSamples} / ${live.totalSamples}`
            : '—'}
        </span>
        <span className="text-text-muted">Batería</span>
        <span className="text-right font-mono">
          {live.battery
            ? `${live.battery.state} · ${(live.battery.voltageMv / 1000).toFixed(2)} V`
            : typeof batteryLevel === 'number'
              ? `${batteryLevel}%`
              : '—'}
        </span>
        <span className="text-text-muted">Notificaciones / cadencia</span>
        <span className="text-right font-mono">
          {live.notifies}
          {live.notifyIntervalMs !== null
            ? ` · ${live.notifyIntervalMs.toFixed(0)} ms`
            : ''}
        </span>
        <span className="text-text-muted">Última notificación</span>
        <span className="text-right font-mono">
          {live.lastNotifyAt
            ? new Date(live.lastNotifyAt).toLocaleTimeString('es-AR')
            : '—'}
        </span>
      </div>
      {error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-sm py-xs text-xs text-rose-400">
          {error}
        </div>
      )}
      {connected && live.measurement?.quality === 'saturated_high' && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-sm py-xs text-xs text-amber-400">
          ADC saturado en el rango {live.measurement.rangeId}: la resistencia
          está por debajo del mínimo de ese rango. Si el firmware está en
          AUTO_RANGE tendría que bajar de ganancia solo; si no, es contacto o
          rango fijo mal elegido.
        </div>
      )}
      {connected && live.measurement?.quality === 'near_zero' && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-sm py-xs text-xs text-amber-400">
          ADC en cero: circuito abierto. Electrodos sin contacto o cable
          desconectado.
        </div>
      )}
      {live.quality && live.quality.samples > 1 && (
        <div className="mt-xs border-t border-surface-muted pt-xs">
          <div className="mb-xs text-xs uppercase tracking-wide text-text-muted">
            Calidad de la señal ({live.quality.samples} muestras ·{' '}
            {live.quality.durationS.toFixed(0)} s)
          </div>
          <div className="grid grid-cols-2 gap-x-md gap-y-xs">
            <span className="text-text-muted">Cadencia real (nominal 10 Hz)</span>
            <span className="text-right font-mono">
              {live.quality.effectiveHz !== null
                ? `${live.quality.effectiveHz.toFixed(2)} Hz`
                : '—'}
              {live.quality.medianIntervalMs !== null
                ? ` · med ${live.quality.medianIntervalMs.toFixed(0)} ms`
                : ''}
            </span>
            <span className="text-text-muted">Jitter p95 / notifies perdidos</span>
            <span className="text-right font-mono">
              {live.quality.p95IntervalMs !== null
                ? `${live.quality.p95IntervalMs.toFixed(0)} ms`
                : '—'}{' '}
              · {live.quality.gaps}
            </span>
            <span className="text-text-muted">Ruido (RMS de diferencias sucesivas)</span>
            <span className="text-right font-mono">
              {live.quality.adcNoiseRmsLsb !== null
                ? `${live.quality.adcNoiseRmsLsb.toFixed(2)} LSB`
                : '—'}
              {live.quality.conductanceNoiseRmsUS !== null
                ? ` · ${live.quality.conductanceNoiseRmsUS.toFixed(4)} µS`
                : ''}
            </span>
            <span className="text-text-muted">Saturadas / en cero</span>
            <span className="text-right font-mono">
              {live.quality.saturatedSamples} / {live.quality.nearZeroSamples} (
              {(live.quality.invalidFraction * 100).toFixed(1)} % inválidas)
            </span>
            <span className="text-text-muted">Cambios de rango / antes del settle</span>
            <span className="text-right font-mono">
              {live.quality.rangeSwitches} / {live.quality.settleViolations}
            </span>
            <span className="text-text-muted">SCL Pico / conductancia ADC</span>
            <span className="text-right font-mono">
              {live.quality.sclOverAdcRatio !== null
                ? `×${live.quality.sclOverAdcRatio.toFixed(3)}`
                : 'sin SCL'}
            </span>
          </div>
          {live.quality.conductanceNoiseRmsUS !== null &&
            live.quality.medianConductanceUS !== null &&
            live.quality.medianConductanceUS > 0 && (
              <div className="mt-xs text-xs text-text-muted">
                El ruido es el{' '}
                {(
                  (live.quality.conductanceNoiseRmsUS /
                    live.quality.medianConductanceUS) *
                  100
                ).toFixed(2)}{' '}
                % del nivel tónico. Un SCR de estrés mueve del orden del 1-5 %: si el
                ruido se le acerca, la respuesta no se va a poder separar.
              </div>
            )}
          {live.quality.effectiveHz !== null &&
            live.quality.effectiveHz < 8 && (
              <div className="mt-xs rounded-md border border-amber-500/30 bg-amber-500/10 px-sm py-xs text-xs text-amber-400">
                Cadencia por debajo de 8 Hz contra los 10 Hz nominales: el enlace BLE
                está perdiendo notificaciones.
              </div>
            )}
          {live.quality.rangeSwitches > live.quality.samples / 20 && (
            <div className="mt-xs rounded-md border border-amber-500/30 bg-amber-500/10 px-sm py-xs text-xs text-amber-400">
              El autorango está golpeando de un rango a otro: la señal está sentada
              sobre un umbral de histéresis y el salto entre rangos se va a leer como
              una respuesta que no existe.
            </div>
          )}
        </div>
      )}
      {live.sclTrace.length >= 2 && (
        <div>
          <div className="text-xs text-text-muted">SCL (µS, Pico)</div>
          <Sparkline values={live.sclTrace} />
        </div>
      )}
      {live.conductanceTrace.length >= 2 && (
        <div>
          <div className="text-xs text-text-muted">Conductancia desde el ADC (µS, sólo muestras válidas)</div>
          <Sparkline values={live.conductanceTrace} />
        </div>
      )}
      {live.adcTrace.length >= 2 && (
        <div>
          <div className="text-xs text-text-muted">ADC crudo (0–4095)</div>
          <Sparkline values={live.adcTrace} />
        </div>
      )}
    </div>
  )
}
